import {PresenzeCausaleWFRecord} from "@commons/mapping/PresenzeCausaleWF";
import React, {Dispatch, SetStateAction} from "react";
import DTTable from "@components/dataTable/Table";
import classNames from "classnames";
import * as Buttons from "@components/dataTable/buttons";
import {DateTime} from "luxon";
import {UserCRUDRecord} from "@commons/mapping/UserCRUDRecord";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectUser} from "@commons/core.slice";
import {PageType} from "./wf.dashboard.detail";
import TooltipEx from "@components/Tooltip";
import WFIcons from "@components/WFIcons";
import DTTitle from "@components/dataTable/TableTitle";
import Pagination from "@components/dataTable/Pagination";
import {PaginationReturnType} from "@components/dataTable/usePagination";
import {FPNetDate} from "@commons/types";
import {IconAlertTriangle, IconArrowRight, IconCheck, IconInfoCircleFilled} from "@tabler/icons-react";
import {useCausaliWF} from "@commons/services/presenze_api/workflow.queries";
import {CausaleWFRecord} from "@commons/mapping/CausaleWFRecord";
import {MonitorWFData} from "@commons/services/presenze_api/monitorWF.api";
import {ManutenzioneTimbrature} from "@commons/mapping/ManutenzioneTimbrature";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";

interface TableProps {
    loadResource: (uri: string) => void,
    deleteResource: (uri: string) => void,
    URI: string | null,
    data?: MonitorWFData,
    currentDay?: string | null,
    page: PageType,

    /*PROP PER PAGINA MONITOR WF*/
    pagination?: Pick<PaginationReturnType, 'search' | 'pageLength' | 'page' | 'setPage' | 'setPageLength' | 'setSearch'>,
    tableTitle?: string,
    isEditabile?: (currentDay: FPNetDate) => boolean, //Se page è MONITOR WF, testo per ogni richiesta se editabile o meno!
    alertEditabile?: React.ReactNode | string,
    selected?: number[],
    setSelected?: Dispatch<SetStateAction<number[]>>,
    showFuoriTermine?: boolean,
}

const ToggleSelelection: React.FC<{ item: PresenzeCausaleWFRecord, selected?: number[], setSelected?: Dispatch<SetStateAction<number[]>>, }> = ({item, selected, setSelected,}) => {
    const {data: causaliWF} = useCausaliWF();
    if (!setSelected)
        return null;
    if (!item.wf.annullata && (item.wf.stato === 'IN_ATTESA' || item.wf.stato === 'IN_VALUTAZIONE')) {
        const senzaNote = causaliWF &&
            causaliWF.find(causale => causale.codice === item.codice && (CausaleWFRecord.notaObbligApprovatore(causale)!) === 0); // senza note obbligatorie per il responsabile
        return senzaNote ?
            <input className={"ml-2"} type="checkbox" checked={selected?.find(id => id === item.wf.idWF) ? true : false}
                   onChange={() => {
                       setSelected((current) => {
                           if (current!.indexOf(item.wf.idWF, 0) > -1) {
                               return current.filter(id => id !== item.wf.idWF);
                           } else {
                               return [...current, item.wf.idWF];
                           }
                       });
                   }}/>
            :
            <span className="ml-2 text-blue-400 text-xs">Note obblig.</span>
    } else
        return null;
}
/**
 * Funzione che aggiunge i dati da gestione presenze, se la richiesta è elaborata
 * @param causale
 * @param dettaglio
 */
const addDatiPresenze = (causale: PresenzeCausaleWFRecord, dettaglio: 'quantOraria' | 'giust' | 'causaleHR') => {
    if (dettaglio === "quantOraria") {
        return <span className="inline-flex text-sky-400 text-sm items-center">
                <IconArrowRight className="w-4"/>{causale.quantitaGiustificativo}hh
            </span>
    }
    if (dettaglio === "giust") {
        return <span className="inline-flex text-sky-400 text-sm items-center">
                <IconArrowRight className="w-4"/>{causale.codiceGiustificativo}
            </span>
    }
    if (dettaglio === "causaleHR") {
        return <span className="inline-flex text-sm items-center">
                <IconArrowRight className="w-4"/>{causale.codiceHR}
            </span>
    }
}

const getOrario = function (causale: PresenzeCausaleWFRecord, isMonitor: boolean = true) {
    const formatHHMM = function (value: string | null | undefined, defIfNUll: string = '00:00') {
        if (value && value.length === 4) {
            const ore = value.substring(0, 2), minuti = value.substring(2);
            return ore + ':' + minuti;
        } else {
            return defIfNUll;
        }
    };

    const dataRif = DateTime.fromISO(causale.giornoDt!).toFormat("dd/LL/yyyy");

    const dtOrarioClass = 'inline-flex text-center items-center';

    if (causale.tipoCausale === 'ORE_MANCANTI' || causale.tipoCausale === 'ORE_ECCEDENTI') {
        if (!causale.giornataIntera) {
            const entrata = causale.oraInizio;
            const uscita = causale.oraFine;
            let orario = '';
            if (!(entrata === '0000' && uscita === '0000')) {
                orario = formatHHMM(entrata) + ' - ' + formatHHMM(uscita);
            }

            orario = !isMonitor ? (orario + ', ' + causale.quantita + ' hh') : orario;

            return !isMonitor ? orario :
                <span className={dtOrarioClass}>{dataRif} -&nbsp;<span className="text-xs">{orario}</span>&nbsp;{causale.elaborato && addDatiPresenze(causale, 'quantOraria')}</span>;

        } else {
            const giorno = 'Tutto il giorno';
            return !isMonitor ? giorno :
                <span className={dtOrarioClass}>{dataRif}&nbsp;{causale.elaborato && addDatiPresenze(causale, 'quantOraria')}</span>
        }
    } else if (causale.tipoCausale === 'QUANTITA') {
        const quantita = causale.quantita !== 0 ? `- ${causale.quantita}` : ''
        return `${dataRif} ${quantita}`
    } else
        return '-';
};

/**
 * Componente per la tabella del dettaglio giornaliero
 * @param loadResource
 * @param deleteResource
 * @param URI
 * @param data instanza di MonitorWFData (contiene l'intero periodo, nn solo il giorno selezionato)
 * @param currentDay data del giorno in esame in formato stringa aaaa-mm-gg, es. "2023-05-01"
 * @param page
 * @param pagination
 * @param tableTitle
 * @param isEditabile
 * @param alertEditabile
 * @param selected
 * @param setSelected
 * @constructor
 */export const TableRichiesteWF: React.FC<TableProps> = ({
                                                              loadResource, deleteResource, URI, data,
                                                              currentDay, page,
                                                              pagination, tableTitle,
                                                              isEditabile, alertEditabile,
                                                              selected, setSelected,
                                                              showFuoriTermine,
                                                          }) => {

    const user: UserCRUDRecord | null = useAppSelector(selectUser); // User corrente
    const isMonitor = page === 'monitorWF';

    const getNote = function (causale: PresenzeCausaleWFRecord) {
        if (causale && causale.wf && causale.wf.livelli) {
            const soggetto = causale.wf.livelli[0].richiedente;
            if (soggetto && soggetto.note) {
                return soggetto.note;
            }
        }
        return null;
    }

    const isInvalidable = (full: PresenzeCausaleWFRecord, readOnly: boolean): boolean => {
        if (!user) return false;

        switch (user.userClass) {
            case "ADMIN":
            case "ALIAS":
            case "SUPER":
            case "COMPANY_OPERATOR":
                return true;
            default:
                break;
        }
        // BRANCH_OPERATOR (nessun caso reale) o EMPLOYEE
        let isEditable = !readOnly;
        if (isEditable) {
            //if (user.userName === full.wf.insertTrace.userName) { fino al 2/4/2024
            if (user.userName === full.codiceFiscale) { // Se sto operando su me stesso
                const causaleWF = full.causaleWF;
                return !!((full.wf.stato === 'IN_ATTESA') ||
                    ((causaleWF && causaleWF.statoIniziale === "ACCETTATA") && !full.elaborato));
            } else { // Sono un responsabile e opero su altri lavoratori
                if (!full.wf.insertTrace) {
                    return false;
                }
                if ((full.wf.insertTrace.userName === full.codiceFiscale) || // Richiesta inserita dal lavoratore
                    (full.wf.insertTrace.userName === user.userName)) { // Richiesta inserita dal responsabile per il lavoratore
                    return (!full.elaborato);
                } else {
                    return false;
                }
            }
        }
        return isEditable;
    };

    // utilizzo la proprietà "filter" di DTTable per filtrare solo il giorno corrente! NB: "filter" non serve per la pagina monitorWF!!!
    const filtraSoloGiornoCorrente = (richiesta: PresenzeCausaleWFRecord) => richiesta.giornoDt === currentDay;
    return <>
        {page === 'monitorWF' && pagination && <span>
            <DTTitle id="elencoRichiesteMonitorWF" title={tableTitle} extraNode={alertEditabile}
                     {...{currentPageLength: pagination.pageLength, setPageLength: pagination.setPageLength, currentSearch: pagination.search, setSearch: pagination.setSearch}}
            />
        </span>}

        <DTTable<PresenzeCausaleWFRecord> dtSchema={data} filter={!isMonitor ? filtraSoloGiornoCorrente : undefined}
                                          header={<>
                                              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                  Id WF
                                              </th>
                                              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                  Orario rif./quantit&agrave;
                                              </th>
                                              {isMonitor && <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                  Nominativo
                                              </th>}
                                              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                  Causale/note
                                              </th>
                                              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                  Stato
                                              </th>
                                              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                  Elaborato
                                              </th>
                                              <th scope="col" className="py-3.5 w-14"/>
                                          </>}

                                          rows={({item, index, isMobile, currentRow, register}) => {
                                              const isSelfUser = (!isMonitor && item.codiceFiscale === user?.codiceFiscale) && user?.userClass === 'EMPLOYEE';
                                              const deleteBtn = <Buttons.Delete onDelete={() => deleteResource(item.URI!)}/>;
                                              const editBtn = <Buttons.Edit onAction={() => loadResource(item.URI!)}/>;
                                              const showBtn = <Buttons.Show onAction={() => loadResource(item.URI!)}/>;
                                              const readOnly = isEditabile ? !isEditabile(item.giornoDt) : false;
                                              return <tr {...register(item.wf.idWF, index, item.URI === URI)}
                                                         key={item.wf.idWF + (item.giornoDt || item.giornataIntera.toString())}>
                                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                      <span
                                                          className={classNames(item.wf.annullata && 'line-through')}>#{item.wf.idWF} {showFuoriTermine && readOnly ? 'fuori termine' : ''}</span>
                                                      <ToggleSelelection item={item} selected={selected} setSelected={setSelected}/>
                                                  </td>

                                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                        <span className={classNames(item.wf.annullata && 'line-through')}>
                                                            {getOrario(item)}
                                                        </span>
                                                  </td>

                                                  {isMonitor && <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                      <span className={classNames(item.wf.annullata && 'line-through')}>{item.cognome} {item.nome}</span>
                                                  </td>
                                                  }

                                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6 inline-flex">
                                                         <span className={classNames(item.wf.annullata && 'line-through')}>
                                                             {page !== "monitorWF" &&
                                                                 <span className="inline-flex items-center">
                                                                 {item.descrizioneCausale} [{item.codice}
                                                                     {item.codiceHR && addDatiPresenze(item, 'causaleHR')}
                                                                     ]
                                                                 </span>}
                                                             {page == "monitorWF" &&
                                                                 <span className="inline-flex items-center">
                                                                     {item.codice} {item.codiceHR && addDatiPresenze(item, 'causaleHR')}
                                                                     {item.elaborato && item.codice !== item.codiceGiustificativo && addDatiPresenze(item, 'giust')}
                                                                 </span>}
                                                         </span>
                                                      <TooltipEx tooltip={getNote(item)} placement="bottom" hideIfNull={true}>
                                                          <IconInfoCircleFilled className="ml-1 h-5 w-5 text-blue-500"/>
                                                      </TooltipEx>
                                                  </td>
                                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6 inline-flex">
                                                      <WFIcons wfStato={item.wf.stato!}/>
                                                      <ShowAlertMT richiesta={item} day={item}></ShowAlertMT>
                                                  </td>
                                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                      {item.elaborato && <IconCheck className="h-5 w-5" aria-hidden="true"/>}
                                                  </td>

                                                  <td className="whitespace-nowrap py-2 text-sm font-medium">
                                                      <Buttons.Visibility visible={isMobile || index === currentRow}>
                                                          {(isSelfUser || readOnly || item.wf.annullata) && showBtn}
                                                          {(!isSelfUser && !readOnly && !item.wf.annullata) && editBtn}
                                                          {isInvalidable(item, readOnly) && deleteBtn}
                                                          {/*
                                                          {(isSelfUser && !readOnly && item.wf.stato === 'IN_ATTESA' && !item.wf.annullata) && deleteBtn}
                                                          {(!isSelfUser && !readOnly && !item.wf.annullata) && deleteBtn}
*/}
                                                      </Buttons.Visibility>
                                                  </td>
                                              </tr>
                                          }}
        />
        {(pagination && page === 'monitorWF') &&
            <Pagination dataTables={data} currentPage={pagination.page} setPage={pagination.setPage} currentPageLength={pagination.pageLength}></Pagination>}
    </>
}

export const ShowAlertMT: React.FC<{ richiesta: PresenzeCausaleWFRecord, day: Pick<PresenzeCausaleWFRecord | PresenzeGiornataPlusRecord, 'effettiveOriginaliBackup'> }> = ({
                                                                                                                                                                               richiesta,
                                                                                                                                                                               day
                                                                                                                                                                           }) => {
    if (!richiesta.wf.annullata && richiesta.tipoCausale === 'MANUTENZIONE_TIMBRATURE') {
        const mt = ManutenzioneTimbrature.factory();
        ManutenzioneTimbrature.ripristinaDaRichiesta(mt, richiesta);
        if (ManutenzioneTimbrature.effettiveOriginaliIsChanged(mt, day)) {
            return <TooltipEx tooltip="Attenzione! Le timbrature attuali sono diverse da quelle valutate al momento del caricamento della richiesta!">
                <IconAlertTriangle className="ml-1 h-5 w-5 text-danger"/>
            </TooltipEx>;
        }
    }
    return <></>;
};
